'use client'

// Lake — the pond, grown into a lake. A single horizontal plane with a
// stylised (anime) TSL water material:
//   • flat anime water — one bright colour, unlit MeshBasicNodeMaterial so it
//     stays graphic; darkened by mood.nightness at night.
//   • depth-detected SHORELINE FOAM — samples the scene depth behind the water
//     and paints a white band wherever the lakebed / an object rises close to
//     the surface. The band OSCILLATES in width (0.65 ↔ 1.15) with a spatial
//     phase, so the shoreline laps in and out and the water feels alive.
//   • reactive ripple RINGS — call triggerLakeRipple(x,z) (e.g. when Victor
//     paddles / an object drops in) to send an expanding ring from that point.
//   • BIOLUMINESCENCE after dark. Same three masks, different job: the body
//     drops to near black, the lapping band burns cyan instead of white, and
//     loose plankton light wherever the water is worked. See the block below.
//
// Cheap: one 2-tri plane, all per-fragment, no vertex work. Runs through
// RendererDither like the rest of the world. Links to activity_pond; jetty /
// lily-pad objects sit on top and get their own foam ring.

import { useMemo, useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn, If, uniform, vec2, vec3, float, mix, smoothstep, clamp, abs, length, step, sin, max,
  color, positionWorld, positionView, viewportDepthTexture, perspectiveDepthToViewZ,
  cameraNear, cameraFar, mx_noise_float,
} from 'three/tsl'
import { mood } from '../../../lib/world/mood'
import useGameStore from '../../../stores/gameStore'

const POSITION = [-73.65, -0.32, -0.68]
const SIZE = [40, 45] // world X × Z

// ── look ─────────────────────────────────────────────────────
const WATER = new THREE.Color('#79a1ad')
const FOAM = new THREE.Color('#eafdff')
const FOAM_MID = 0.9        // foam width oscillates FOAM_MID ± FOAM_AMP → 0.65..1.15
const FOAM_AMP = 0.25
const FOAM_OSC_SPEED = 0.6  // how fast the shoreline breathes
const NIGHT_DIM = 0.5       // the old night: a dimmed day lake, and still what
                            // a night looks like before the wish arrives
// ── ripples ──────────────────────────────────────────────────
const MAX_RIPPLES = 5
const RING_SPEED = 2.6
const RING_LIFE = 2.4
const RING_THICK = 0.5

// ── bioluminescence ──────────────────────────────────────────
//
// Night used to be the day lake multiplied by 0.5, which is a grey version
// of noon rather than a lake after dark. It is plankton water instead now,
// built out of the masks that already exist rather than new ones:
//
//   the BODY drops toward near black, so a glow has something to read
//     against. A bright pool at midnight was the thing killing it.
//   the DEPTH BAND (the same oscillating shoreline foam) carries a cyan
//     burn with a near-white crest on its inner half. That is where the
//     real thing concentrates: agitation plus shallow water, right at the
//     waterline, and it laps in and out because the band already does.
//   the RIPPLE RINGS light too, which is the honest version of the effect.
//     Bioluminescence is mechanically triggered, so anything that disturbs
//     the water should leave a lit ring behind it.
//   loose CELLS drift through the open water on a noise field, only the
//     top of the field lighting, each blinking on its own phase. They are
//     weighted by how worked the water is, so they crowd the band and the
//     rings and only glitter faintly out in the middle.
//
// It all rides mood.nightness, so daylight is untouched, and it goes into
// emissiveNode rather than more colour for two reasons: it ADDS over the
// body (a burn, not a tint), and it is exactly the channel the selective
// bloom pass reads off the MRT (?bloom=1), so the halo comes free the day
// that flag flips. Without bloom it still reads, because the values are
// tuned for LDR against a dark body rather than for a blur to rescue.
// A bloom is never one colour. What is in the water decides it, so a real
// shoreline runs sea green through cyan into violet along its own length,
// and all three turn up in the same photograph. So the hue is a second,
// very slow, very large noise field: cyan is the base, low patches pull
// toward green and high ones toward violet, and it drifts, so the lake
// never settles into a pattern you can learn. The open-water cells read
// the SAME field inverted, which costs nothing and means the loose
// plankton are never the colour the shore is doing right there.
const NIGHT_WATER = new THREE.Color('#08202c') // the body of the lake after dark
const BIO = new THREE.Color('#3fe4ff')         // the base of the bloom
const BIO_GREEN = new THREE.Color('#3dffb4')   // where it goes sea green
const BIO_VIOLET = new THREE.Color('#b070ff')  // where it goes purple
const BIO_HOT = new THREE.Color('#dcffff')     // the crest of the band, near white
const BIO_EDGE = 0.85       // burn along the lapping depth band
const BIO_HOT_GAIN = 0.3    // the thin white line on the inner edge of that band
const BIO_RIPPLE = 1.0      // a ring of cells lighting where something moved
const BIO_SPARK = 0.9       // loose cells out in the open water
const NIGHT_DARK = 1.0      // how far the body goes toward NIGHT_WATER
const HUE_SPREAD = 1.0      // 0 = flat cyan, 1 = the full green..violet range
// One patch per ~10 world units. Sized against the lake (40x45) rather than
// picked: at half this the whole pond fell inside a single patch and the
// colour varied over time but never across the water, which is the one place
// it needs to.
const HUE_FREQ = 0.1
const HUE_GAIN = 2.1        // stretches the noise so it reaches both ends
const HUE_DRIFT = 0.012     // how fast the colours wander
const SPARK_FREQ = 0.9      // cells per world unit
const SPARK_DRIFT = 0.06    // how fast the field slides with the water
const SPARK_BLINK = 2.4     // twinkle rate
const SPARK_REST = 0.18     // how much undisturbed water still glitters
// How fast the water takes it up when the wish lands. Slow on purpose: this
// is the one moment the whole thing exists for, and a boolean flipping the
// lake on between two frames is a switch being thrown rather than something
// arriving.
const WISH_EASE = 0.25

// Module-level handles so triggerLakeRipple can reach the live uniforms.
let _ripples = null
let _timeU = null
let _cursor = 0

/** Send an expanding ripple ring from world (x,z). Wire to the bird entering
 *  the lake / objects dropping in. No-op until the Lake has mounted. */
export function triggerLakeRipple(x, z) {
  if (!_ripples || !_timeU) return
  _ripples[_cursor % _ripples.length].value.set(x, z, _timeU.value, 1)
  _cursor++
}

export function Lake() {
  const uTime = useMemo(() => uniform(0), [])
  const uRipples = useMemo(
    () => Array.from({ length: MAX_RIPPLES }, () => uniform(new THREE.Vector4(0, 0, -999, 0))),
    [],
  )
  // The four numbers worth arguing about, as uniforms so the art pass is a
  // console line rather than a rebuild. setBio() below.
  const uEdge = useMemo(() => uniform(BIO_EDGE), [])
  const uSpark = useMemo(() => uniform(BIO_SPARK), [])
  const uRing = useMemo(() => uniform(BIO_RIPPLE), [])
  const uDark = useMemo(() => uniform(NIGHT_DARK), [])
  const uHue = useMemo(() => uniform(HUE_SPREAD), [])
  // WHETHER THE WISH HAS ARRIVED. The sim owns this: he asked the octopus
  // for something and the water doing it before that was granted would make
  // the grant meaningless. Defaults to 1 with NO sim connected, which is the
  // /director and any dev session, so an art pass never has to run a server
  // to see the thing it is art-directing.
  const uWish = useMemo(() => uniform(1), [])
  const wishTarget = useRef(1)
  // The holders can light the pond in daylight ("Light up the pond"): the
  // sim sends pondGlow while it is on, and the glow reads as if it were
  // night on the water alone, the rest of the town still in its day.
  const uDayGlow = useMemo(() => uniform(0), [])
  const dayGlowTarget = useRef(0)

  const material = useMemo(() => {
    const m = new THREE.MeshBasicNodeMaterial()
    m.transparent = true

    const w = positionWorld.xz
    const night = mood.nightness
    // How lit the water is: night, or the holders' daytime glow.
    const lit = max(night, uDayGlow)

    // Water column thickness: scene depth behind the plane vs this fragment.
    const sceneViewZ = perspectiveDepthToViewZ(viewportDepthTexture(), cameraNear, cameraFar)
    const waterDepth = positionView.z.sub(sceneViewZ).abs()

    // Reactive ripple rings (compile-time unrolled over the slots).
    let ring = float(0)
    for (const uR of uRipples) {
      const d = length(w.sub(vec2(uR.x, uR.y)))
      const age = uTime.sub(uR.z)
      const front = age.mul(RING_SPEED)
      const band = smoothstep(float(RING_THICK), float(0), abs(d.sub(front)))
      const alive = uR.w.mul(smoothstep(float(RING_LIFE), float(0), age)).mul(step(float(0), age))
      ring = ring.add(band.mul(alive))
    }
    ring = clamp(ring, 0, 1)

    // Depth-based shoreline foam. Width oscillates with a spatial phase so the
    // shore laps in and out (that's the "moving water" now), plus a small noise
    // wobble for a hand-drawn edge.
    const phase = uTime.mul(FOAM_OSC_SPEED).add(w.x.mul(0.25)).add(w.y.mul(0.2))
    const foamW = float(FOAM_MID).add(sin(phase).mul(FOAM_AMP))
    const foamNoise = mx_noise_float(vec3(w.mul(1.9), uTime.mul(0.25))).mul(0.5).add(0.5)
    const foamEdge = foamW.mul(foamNoise.mul(0.25).add(0.9)) // × 0.9..1.15 organic
    const foam = smoothstep(foamEdge, float(0), waterDepth)

    // ── body ───────────────────────────────────────────────────
    // Three states, not two: day, a night before the wish, and a night
    // after it. The middle one is the lake exactly as it was, because a
    // near-black pond with nothing lighting in it is not an improvement,
    // it is a hole in the world.
    const nightCol = mix(color(WATER).mul(NIGHT_DIM), color(NIGHT_WATER), uWish.mul(uDark))
    let col = mix(color(WATER), nightCol, max(night, uDayGlow.mul(0.55)))

    // What the lapping is made of: white foam by day, and after dark either
    // the old dimmed white or a dim cyan, kept low so the band has some body
    // under it without arguing with the hue the emissive paints on top.
    const lapNight = mix(color(FOAM).mul(NIGHT_DIM), color(BIO).mul(0.28), uWish)
    const lap = mix(color(FOAM), lapNight, night)
    col = mix(col, lap, ring.mul(0.6))
    col = mix(col, lap, foam)

    m.colorNode = col

    // ── the glow ───────────────────────────────────────────────
    // Branched on a uniform, so the plankton pass costs nothing in daylight
    // (deep night is ~18% of the cycle, see mood.nightnessFor). Safe to
    // branch: nothing in here samples a texture, it is all arithmetic.
    m.emissiveNode = Fn(() => {
      const glow = vec3(0).toVar()

      If(lit.mul(uWish).greaterThan(0.002), () => {
        // What colour the bloom is here. One big slow field, read forwards
        // for the shore and backwards for the open water, so the two are
        // never the same colour in the same place.
        // HUE_GAIN is doing real work: simplex only rarely reaches its own
        // extremes, so the raw field sits in a narrow band around the middle
        // and the first pass came out cyan everywhere. Stretching it first is
        // what puts actual green and actual violet on the water.
        const hue = clamp(
          mx_noise_float(vec3(w.mul(HUE_FREQ), uTime.mul(HUE_DRIFT))).mul(HUE_GAIN).mul(0.5).add(0.5),
          0, 1,
        )
        const tintAt = (h) => mix(
          mix(color(BIO), color(BIO_GREEN), smoothstep(float(0.46), float(0.18), h).mul(uHue)),
          color(BIO_VIOLET),
          smoothstep(float(0.54), float(0.8), h).mul(uHue),
        )
        const shoreTint = tintAt(hue)
        const cellTint = tintAt(hue.oneMinus())

        // The cells. One drifting field, only its top lighting up, each
        // blinking on a phase read out of the field itself so no two go at
        // once.
        const cell = mx_noise_float(vec3(w.mul(SPARK_FREQ), uTime.mul(SPARK_DRIFT))).mul(0.5).add(0.5)
        const blink = sin(cell.mul(31).add(uTime.mul(SPARK_BLINK))).mul(0.5).add(0.5)
        const spark = smoothstep(float(0.62), float(0.9), cell).mul(blink.mul(0.7).add(0.3))

        // Where they light: the lapping band, a ripple going past, and a low
        // rest level so the open water is never completely dead.
        const worked = clamp(foam.mul(1.5).add(ring.mul(1.3)).add(SPARK_REST), 0, 2)

        // Plankton are patchy, so the band is not evenly bright along its
        // length. Reuses the foam noise rather than paying for a second field.
        const patch = foamNoise.mul(0.7).add(0.5)

        // The thin line on the inner edge of the band, which is where it
        // burns out toward white. Narrow and only PART way to white: a wide
        // white ribbon clips every hue back out of the shot, which is what
        // the first pass did.
        const crest = smoothstep(float(0.62), float(0.97), foam)
        const hotTint = mix(color(BIO_HOT), shoreTint, 0.35)

        glow.assign(
          shoreTint.mul(foam.mul(patch).mul(uEdge))
            .add(hotTint.mul(crest.mul(patch).mul(uEdge).mul(BIO_HOT_GAIN)))
            .add(shoreTint.mul(ring.mul(uRing)))
            .add(cellTint.mul(spark.mul(worked).mul(uSpark)))
            .mul(lit).mul(uWish),
        )
      })

      return glow
    })()

    // Solid flat water — no opacity node (opacity stays 1). `transparent` is
    // kept ONLY so it draws after the opaque pass and its depth read sees the
    // lakebed behind it for the foam; visually it's fully opaque.
    return m
  }, [uTime, uRipples, uEdge, uSpark, uRing, uDark, uHue, uWish, uDayGlow])

  useEffect(() => {
    _ripples = uRipples
    _timeU = uTime
    if (typeof window !== 'undefined') window.lakeRipple = triggerLakeRipple
    return () => { _ripples = null; _timeU = null }
  }, [uRipples, uTime])

  // Has the wish arrived? Read imperatively, the same way GreenStone reads
  // its resonance: a hook here would re-render the component (and rebuild
  // the material) on every unrelated store write, and this answer changes
  // roughly once, ever.
  useEffect(() => {
    const apply = (s) => {
      // ONLY AN EXPLICIT FALSE TURNS THE WATER OFF, and the first version of
      // this got it wrong in a way worth writing down: it treated "there is
      // an environment object" as "a sim is talking to us", and the director
      // writes its own partial environment straight into this store (that is
      // how its time-of-day slider works). So the whole lake went dark on the
      // one page built for art-directing it.
      //
      // A real sim always sends the field, because serializeEnvironment
      // always writes it. Missing therefore means nobody authoritative has an
      // opinion here: the director, a dev session, a socket that has not
      // landed yet. Show the water at its best in all of those. The gate
      // exists to protect the ARRIVAL, and there is no arrival to protect in
      // a session with no world running.
      const flag = s.environment?.pondBloom
      wishTarget.current = flag === undefined ? 1 : (flag ? 1 : 0)
      dayGlowTarget.current = s.environment?.pondGlow ? 1 : 0
    }
    apply(useGameStore.getState())
    return useGameStore.subscribe(apply)
  }, [])

  // Dev-only art pass, same habit as setBloom / pinNight. Hold the world at
  // night first (pinNight(1)), then dial:
  //   setBio(edge, spark, ripple, dark, hue)
  // hue is the colour spread: 0 holds the whole lake at cyan, 1 lets the
  // green and violet patches through.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    window.setBio = (edge, spark, ripple, dark, hue) => {
      if (edge != null) uEdge.value = edge
      if (spark != null) uSpark.value = spark
      if (ripple != null) uRing.value = ripple
      if (dark != null) uDark.value = dark
      if (hue != null) uHue.value = hue
      return `bio edge ${uEdge.value} spark ${uSpark.value} ripple ${uRing.value} dark ${uDark.value} hue ${uHue.value}`
    }
    return () => { delete window.setBio }
  }, [uEdge, uSpark, uRing, uDark, uHue])

  useFrame((_, dt) => {
    uTime.value += Math.min(dt, 0.05)
    // One uniform write a frame, and the lake comes up rather than snapping.
    uWish.value += (wishTarget.current - uWish.value) * Math.min(1, dt * WISH_EASE)
    uDayGlow.value += (dayGlowTarget.current - uDayGlow.value) * Math.min(1, dt * WISH_EASE)
  })

  // compileInFrame: the viewport depth copy runs inside compileAsync and
  // ends a pass that is not open, so the Precompiler builds the lake by
  // drawing it in one real frame behind the veil instead.
  return (
    <mesh position={POSITION} rotation={[-Math.PI / 2, 0, 0]} material={material} renderOrder={2} userData={{ compileInFrame: true }}>
      <planeGeometry args={[SIZE[0], SIZE[1], 1, 1]} />
    </mesh>
  )
}

export default Lake
